import { Injectable } from '@angular/core';
import {
  Claim,
  ClaimLine,
  CreateClaimForm,
  ClaimRequestForm,
  ClaimReq,
  ClaimResponse,
  ClaimDocuments
} from '@ecosure-suite/shared/util-entities';
import { ClaimsApiService } from '@ecosure-suite/shared/data-access';
import { CommonService, Pagination } from '@ecosure-suite/shared/utils-helpers';
import { LogService } from '@ecosure-suite/shared/util-logging';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClaimRequest } from '@ecosure-suite/shared/util-entities';
import { ClaimLinesConverterService } from '@ecosure-suite/shared/back-office/claims/utils-transformations';
import { PolicyHoldersService } from '@ecosure-suite/back-office/individuals/policy-holders/data-access-policy-holders'
import { NotificationsService } from '@ecosure-suite/shared/utils-notifications';
import { BaseApi } from 'libs/shared/data-access/src/lib/abstracts';

@Injectable({
  providedIn: 'root'
})
export class ClaimRequestsService {
  constructor(
    private apiService: ClaimsApiService,
    private common: CommonService,
    private logService: LogService,
    private claimLinesConverter: ClaimLinesConverterService,
    private notification: NotificationsService
  ) { }

  listByIdNumber({
    idNumber,
    page
  }: {
    idNumber: string;
    page: Pagination;
  }): Observable<{ claimRequests: ClaimRequest[]; page: Partial<Pagination> }> {
    return this.apiService
      .get(
        `/v1/claimRequests/${idNumber}/byNationalId?page=${page.currentPage -
        1}&fetchSize=${page.itemsPerPage}`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            throw new Error(data.message);
          }
          return data.statusCode !== '404'
            ? {
              claimRequests: data.responseBody.map(claimRequest => ({
                id: claimRequest.claimRequestId,
                address: claimRequest.policyMemberAddress,
                dateCreated: new Date(claimRequest.creationDate),
                status: claimRequest.claimRequestStatus.replace(/_/g, ' '),
                firstName: claimRequest.firstName,
                lastName: claimRequest.surname,
                idNumber: claimRequest.nationalId,
                gender: claimRequest.gender,
                birthDate: new Date(claimRequest.dateOfBirth),
                msisdn: claimRequest.msisdn,
                createdBy: claimRequest.createdBy,
                assignedTo: claimRequest.assignedTo,
                submittingOffice: claimRequest.submittingOffice
              })) as ClaimRequest[],
              page: {
                currentPage: data.pageNumber + 1,
                totalItems: data.totalElements,
                totalPages: data.pageCount
              } as Pagination
            }
            : { claimRequests: [], page };
        })
      );
  }

  searchDetailedByIdNumber({
    idNumber,
    page
  }: {
    idNumber: string;
    page: Pagination;
  }): Observable<{
    claimRequests: ClaimRequest[];
    claims: Claim[];
    claimLines: ClaimLine[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/v1/claimRequests/detailed?page=${page.currentPage - 1}&fetchSize=${page.itemsPerPage
        }&nationalId=${idNumber}&claimLineStatus=New`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            throw new Error(data.message);
          }

          if (data.statusCode === '404') {
            throw new Error(
              `No claim requests for id number ${idNumber} found!`
            );
          }

          const claimRequests: ClaimRequest[] = [];
          const claims: Claim[] = [];
          const lines: ClaimLine[] = [];

          data.responseBody.elements.forEach(item => {
            claimRequests.push({
              id: item.uiClaimRequest.claimRequestId,
              status: item.uiClaimRequest.claimRequestStatus.replace(/_/g, ' '),
              address: item.uiClaimRequest.policyMemberAddress,
              dateCreated: new Date(item.uiClaimRequest.creationDate),
              createdBy: item.uiClaimRequest.createdBy,
              firstName: item.uiClaimRequest.firstName,
              lastName: item.uiClaimRequest.surname,
              idNumber: item.uiClaimRequest.nationalId,
              gender: item.uiClaimRequest.gender,
              birthDate: new Date(item.uiClaimRequest.dateOfBirth),
              msisdn: item.uiClaimRequest.msisdn,
              assignedTo: item.uiClaimRequest.assignedTo,
              submittingOffice: item.uiClaimRequest.submittingOffice
            });

            item.claimWrappers.forEach(claimItem => {
              claims.push({
                id: claimItem.uiClaim.claimId,
                claimDate: new Date(claimItem.uiClaim.claimDate),
                policyNumber: claimItem.uiClaim.policyNumber,
                suffixNumber: claimItem.uiClaim.suffixNumber,
                claimRequestId: claimItem.uiClaim.claimRequestId,
                status: claimItem.uiClaim.claimStatus.replace(/_/g, ' '),
                memberStatus: claimItem.uiClaim.memberStatus.replace(/_/g, ' '),
                billingStatus: claimItem.uiClaim.billingStatus.replace(
                  /_/g,
                  ' '
                ),
                memberType: claimItem.uiClaim.memberType
              });

              claimItem.claimLineList.forEach(lineItem => {
                lines.push(this.claimLinesConverter.convertClaimLine(lineItem));
              });
            });
          });

          return {
            claimRequests: claimRequests,
            claims: claims,
            claimLines: lines,
            page: {
              currentPage: data.pageNumber + 1,
              totalItems: data.totalElements,
              totalPages: data.pageCount
            }
          };
        })
      );
  }

  getByNationalId({
    idNumber,
    page
  }: {
    idNumber: string;
    page: Pagination;
  }): Observable<{
    claimRequests: ClaimRequest[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/v1/claimRequests/${idNumber}/byNationalId?page=${page.currentPage -
        1}&fetchSize=${page.itemsPerPage}`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }

          return {
            claimRequests: data.responseBody.elements.map(claimRequest => ({
              id: claimRequest.claimRequestId,
              address: claimRequest.policyMemberAddress,
              dateCreated: new Date(claimRequest.creationDate),
              createdBy: claimRequest.createdBy,
              status: claimRequest.claimRequestStatus.replace(/_/g, ' '),
              firstName: claimRequest.firstName,
              lastName: claimRequest.surname,
              idNumber: claimRequest.nationalId,
              gender: claimRequest.gender,
              birthDate: new Date(claimRequest.dateOfBirth),
              msisdn: claimRequest.msisdn,
              assignedTo: claimRequest.assignedTo,
              submittingOffice: claimRequest.submittingOffice
            })),
            page: {
              currentPage: data.responseBody.pageNumber + 1,
              totalItems: data.responseBody.totalElements,
              totalPages: data.responseBody.pageCount
            }
          };
        })
      );
  }

  getById(claimRequestId: number): Observable<ClaimRequest> {
    return this.apiService.get(`/v1/claimRequests/${claimRequestId}`).pipe(
      map(data => {
        if (data.statusCode !== '200') {
          throw new Error(data.message);
        }

        return {
          id: data.responseBody.claimRequestId,
          address: data.responseBody.policyMemberAddress,
          dateCreated: new Date(data.responseBody.creationDate),
          createdBy: data.responseBody.createdBy,
          status: data.responseBody.claimRequestStatus.replace(/_/g, ' '),
          firstName: data.responseBody.firstName,
          lastName: data.responseBody.surname,
          idNumber: data.responseBody.nationalId,
          gender: data.responseBody.gender,
          birthDate: data.responseBody.dateOfBirth
            ? new Date(data.responseBody.dateOfBirth)
            : null,
          msisdn: data.responseBody.msisdn,
          assignedTo: data.responseBody.assignedTo,
          submittingOffice: data.responseBody.submittingOffice
        };
      })
    );
  }

  listByStatus(
    status,
    page): Observable<any> {
    return this.apiService
      .get(
        `/v1/claims/by-claim-status?page=${page}&size=20&searchClaimByStatus=${status.replace(
          / /g,
          '_'
        )}`
      )
      .pipe(
        map(data => {
          return data;
        })
      );
  }

  create({
    address,
    firstName,
    lastName,
    idNumber,
    gender,
    birthDate,
    msisdn,
    office,
    claimLine
  }: Partial<CreateClaimForm>): Observable<{
    claimRequest: ClaimRequest;
    claim: Claim;
    claimLine: ClaimLine;
  }> {
    return this.apiService
      .post(
        '/v1/claimRequests',
        Object.assign(
          {},
          {
            policyMemberAddress: address,
            firstName: firstName,
            surname: lastName,
            nationalId: idNumber,
            gender: gender,
            dateOfBirth: birthDate
              ? CommonService.formatDate(birthDate, 'DD-MM-YYYY')
              : undefined,
            msisdn: msisdn,
            submittingOffice: office
          },
          this.claimLinesConverter.getRequestBody(claimLine)
        )
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            throw new Error(data.message);
          }
          return {
            claimRequest: {
              id: data.responseBody.claimRequestId,
              address: data.responseBody.policyMemberAddress,
              dateCreated: new Date(data.responseBody.creationDate),
              status: data.responseBody.claimRequestStatus
                ? data.responseBody.claimRequestStatus.replace(/_/g, ' ')
                : undefined,
              firstName: data.responseBody.firstName,
              lastName: data.responseBody.surname,
              idNumber: data.responseBody.nationalId,
              gender: data.responseBody.gender,
              birthDate: data.responseBody.dateOfBirth
                ? new Date(data.responseBody.dateOfBirth)
                : null,
              msisdn: data.responseBody.msisdn,
              createdBy: data.responseBody.createdBy,
              assignedTo: data.responseBody.assignedTo,
              submittingOffice: data.responseBody.submittingOffice
            },
            claim: {
              id: data.responseBody.claimId,
              claimRequestId: data.responseBody.claimRequestId,
              claimDate: new Date(data.responseBody.creationDate),
              policyNumber: undefined,
              suffixNumber: undefined,
              status: undefined,
              memberStatus: undefined,
              billingStatus: undefined,
              memberType: undefined
            },
            claimLine: this.claimLinesConverter.convertClaimLine(
              data.responseBody,
              data.responseBody.claimRequestId
            )
          };
        })
      );
  }

  edit(claimRequest, id?): Observable<ClaimResponse | any> {
    return this.apiService
      .put(`/v1/claims/${id}`, claimRequest)
      .pipe(
        map(response => {
          if (response.status == 'OK')
            this.notification.showSuccess('Success', 'Claim request saved successfully.');
          else this.notification.showError('Error', 'Failed to save claim request.');
          return response.data;
        })
      );
  }

  getAllByAssigneeOrStatus({
    page,
    username,
    status
  }: {
    page: Pagination;
    username?: string;
    status?: string;
  }): Observable<{
    claimRequests: ClaimRequest[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/v1/claimRequests/supervisor?page=${page.currentPage - 1}&fetchSize=${page.itemsPerPage
        }${username ? `&user=${username}` : ``}${status ? `&status=${status.replace(/ /g, '_')}` : ``
        }`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            throw new Error(data.message);
          }

          return {
            claimRequests:
              data.statusCode !== '404'
                ? data.responseBody.elements.map(claimRequest => ({
                  id: claimRequest.claimRequestId,
                  address: claimRequest.policyMemberAddress,
                  dateCreated: new Date(claimRequest.creationDate),
                  status: claimRequest.claimRequestStatus.replace(/_/g, ' '),
                  firstName: claimRequest.firstName,
                  lastName: claimRequest.surname,
                  idNumber: claimRequest.nationalId,
                  gender: claimRequest.gender,
                  birthDate: new Date(claimRequest.dateOfBirth),
                  msisdn: claimRequest.msisdn,
                  createdBy: claimRequest.createdBy,
                  assignedTo: claimRequest.assignedTo,
                  submittingOffice: claimRequest.submittingOffice
                }))
                : [],
            page: {
              currentPage: data.responseBody.pageNumber + 1,
              totalItems: data.responseBody.totalElements,
              totalPages: data.responseBody.pageCount
            }
          };
        })
      );
  }

  getAllByCurrentUser(
    page: Pagination
  ): Observable<{
    claimRequests: ClaimRequest[];
    page: Partial<Pagination>;
  }> {
    return this.apiService
      .get(
        `/v1/claimRequests/authFilter?page=${page.currentPage - 1}&fetchSize=${page.itemsPerPage
        }`
      )
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            throw new Error(data.message);
          }

          return {
            claimRequests:
              data.statusCode !== '404'
                ? data.responseBody.elements.map(claimRequest => ({
                  id: claimRequest.claimRequestId,
                  address: claimRequest.policyMemberAddress,
                  dateCreated: new Date(claimRequest.creationDate),
                  status: claimRequest.claimRequestStatus.replace(/_/g, ' '),
                  firstName: claimRequest.firstName,
                  lastName: claimRequest.surname,
                  idNumber: claimRequest.nationalId,
                  gender: claimRequest.gender,
                  birthDate: new Date(claimRequest.dateOfBirth),
                  msisdn: claimRequest.msisdn,
                  createdBy: claimRequest.createdBy,
                  assignedTo: claimRequest.assignedTo,
                  submittingOffice: claimRequest.submittingOffice
                }))
                : [],
            page: {
              currentPage: data.responseBody.pageNumber + 1,
              totalItems: data.responseBody.totalElements,
              totalPages: data.responseBody.pageCount
            }
          };
        })
      );
  }

  assignToVerifier({
    claimRequestId,
    username
  }: {
    claimRequestId: number;
    username: string;
  }): Observable<ClaimRequest> {
    return this.apiService
      .put(`/v1/claimRequests/${claimRequestId}/assign/${username}`)
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            throw new Error(data.message);
          }
          return {
            id: data.responseBody.claimRequestId,
            address: data.responseBody.policyMemberAddress,
            dateCreated: new Date(data.responseBody.creationDate),
            status: data.responseBody.claimRequestStatus.replace(/_/g, ' '),
            firstName: data.responseBody.firstName,
            lastName: data.responseBody.surname,
            idNumber: data.responseBody.nationalId,
            gender: data.responseBody.gender,
            birthDate: data.responseBody.dateOfBirth
              ? new Date(data.responseBody.dateOfBirth)
              : null,
            msisdn: data.responseBody.msisdn,
            createdBy: data.responseBody.createdBy,
            assignedTo: data.responseBody.assignedTo,
            submittingOffice: data.responseBody.submittingOffice
          };
        })
      );
  }

  saveClaimReq(claimReq: ClaimReq): Observable<ClaimResponse> {
    return this.apiService
      .post(`/v1/claims/`, claimReq)
      .pipe(
        map(response => {
          if (response.status == 'OK')
            this.notification.showSuccess('Success', 'Claim request saved successfully.');
          else this.notification.showError('Error', 'Failed to save claim request.');
          return response.data;
        })
      );
  }

  getClaims(): Observable<ClaimResponse[]> {
    return this.apiService
      .get(`/v1/claims/`)
      .pipe(
        map(response => {
          return response.data;
        })
      );
  }

  getClaimByNationalId(nationalId): Observable<ClaimResponse> {
    return this.apiService
      .get(`/v1/claims/by-nationalId/${nationalId}`)
      .pipe(
        map(response => {
          return response.data;
        })
      )
  }

  getClaimDocuments(claimId): Observable<any> {
    return this.apiService
      .get(`/v1/uploads/getClaim-details/${claimId}`)
      .pipe(
        map(response => {
          return response.data;
        }
      )
      )
  }

  getClaimsByPolicyNumber(policyNumber, page): Observable<ClaimResponse[]> {
    return this.apiService
      .get(`/v1/claims/by-policyNumber/${policyNumber}?page=${page}&size=10&policyNumber=${policyNumber}`)
      .pipe(
        map(response => {
          return response;
        })
      )
  }

  getClaimById(id: number): Observable<ClaimResponse> {
    return this.apiService
      .get(`/v1/claims/${id}`)
      .pipe(
        map(response => {
          return response.data;
        })
      );
  }

  uploadFile(file, claimId, fileType) {
    return this.apiService.file(`/v1/uploads/upload-file/${claimId}`, file, { file: file, fileType: fileType })
      .pipe(map(response => {
        if (response.status == 'OK')
          this.notification.showSuccess('Success', `${fileType} Uploaded Successfully!`);
        else this.notification.showError('Failed to Upload', response.message)
        return response;
      }))
  }

  deleteFile(fileId, fileType) {
    return this.apiService.delete(`/v1/uploads/delete-file/${fileId}`)
      .pipe(map(response => {
        if (response.status == 'OK')
          this.notification.showSuccess('Success', fileType + ' Deleted Successfully!');
        else this.notification.showError('An Error Occured', response.message)
        return response;
      }))
  }
  

  getFile(url) {
    return this.apiService.downloadFile(url);
  }

  submitForApproval(data: {claimId, comment, claimStage, claimStatus }){
    return this.apiService.get(`/v1/claims/process/${data.claimId}?claimProcessing=${data.claimStatus}`)
    .pipe(map(response => {
        return this.postComments(data);
    }))
  }

  postComments(data: {claimId, comment, claimStage }){
    return this.apiService.post(`/v1/comments/`, data).subscribe(response => {
      if (response.status == 'OK')
        this.notification.showSuccess('Success', ' Status Updated Successfully!');
      else this.notification.showError('An Error Occured', response.message)
      return response;
    })
  }

}
